import type {
  OptLogActionSnapshot,
  OptLogVolumeActions,
  OptLogVolumeActionsResponse,
  OptLogVolumeActionsTypes,
} from '~/shared/types/api';

import { getOptLogActionMetricKinds } from './optLogActionSnapshot';
import { wireTypeToOptLogDisplayType } from './optLogActionTypeMapping';

export function normalizeOptLogActionType(raw: string): OptLogVolumeActionsTypes {
  const mapped = wireTypeToOptLogDisplayType(raw);
  if (mapped) {
    return mapped;
  }
  return raw as OptLogVolumeActionsTypes;
}

function pickNumber(record: Record<string, unknown>, keys: string[]): number | undefined {
  for (const key of keys) {
    const value = record[key];
    if (value === null || value === undefined || value === '') {
      continue;
    }
    const parsed = typeof value === 'number' ? value : Number(value);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }
  return undefined;
}

export function normalizeOptLogActionSnapshot(raw: unknown): OptLogActionSnapshot | undefined {
  if (raw == null || typeof raw !== 'object') {
    return undefined;
  }
  const record = raw as Record<string, unknown>;
  const diskSizeGb = pickNumber(record, ['diskSizeGb', 'disk_size_gb', 'sizeGb', 'size']);
  const iops = pickNumber(record, ['iops', 'Iops', 'IOPS']);
  const throughput = pickNumber(record, ['throughput', 'Throughput']);
  const performanceTier = pickNumber(record, [
    'performanceTier',
    'performance_tier',
    'tier',
    'arraySize',
    'array_size',
  ]);

  if (
    diskSizeGb == null &&
    iops == null &&
    throughput == null &&
    performanceTier == null
  ) {
    return undefined;
  }

  return { diskSizeGb, iops, throughput, performanceTier };
}

function snapshotsEqual(
  left: OptLogActionSnapshot | undefined,
  right: OptLogActionSnapshot | undefined
): boolean {
  if (left === right) {
    return true;
  }
  if (!left || !right) {
    return !left && !right;
  }
  return (
    left.diskSizeGb === right.diskSizeGb &&
    left.iops === right.iops &&
    left.throughput === right.throughput &&
    left.performanceTier === right.performanceTier
  );
}

export function normalizeOptLogAction(action: OptLogVolumeActions): OptLogVolumeActions {
  const raw = action as OptLogVolumeActions & Record<string, unknown>;
  const type = normalizeOptLogActionType(String(action.type ?? raw.Type ?? ''));
  const before = normalizeOptLogActionSnapshot(raw.before ?? raw.Before);
  const after = normalizeOptLogActionSnapshot(raw.after ?? raw.After);

  if (
    type === action.type &&
    snapshotsEqual(before, action.before) &&
    snapshotsEqual(after, action.after)
  ) {
    return action;
  }

  return {
    ...action,
    type,
    before,
    after,
  };
}

function pickOptLogActionsList(
  response: Partial<OptLogVolumeActionsResponse> & Record<string, unknown>
): OptLogVolumeActions[] {
  const lower = response.actions;
  const upper = response.Actions;

  if (Array.isArray(lower) && lower.length > 0) {
    return lower;
  }
  if (Array.isArray(upper) && upper.length > 0) {
    return upper as OptLogVolumeActions[];
  }
  if (Array.isArray(lower)) {
    return lower;
  }
  if (Array.isArray(upper)) {
    return upper as OptLogVolumeActions[];
  }
  return [];
}

export function extractOptLogActionsFromResponse(
  response: Partial<OptLogVolumeActionsResponse> & Record<string, unknown>
): OptLogVolumeActions[] {
  return pickOptLogActionsList(response);
}

export function normalizeOptLogActions(actions: OptLogVolumeActions[]): OptLogVolumeActions[] {
  let changed = false;
  const normalized = actions.map((action) => {
    const next = normalizeOptLogAction(action);
    if (next !== action) {
      changed = true;
    }
    return next;
  });
  return changed ? normalized : actions;
}

export const optLogMultiMetricRowClass = 'opt-log-multi-metric-row';

export function isOptLogMultiMetricAction(action: OptLogVolumeActions): boolean {
  return getOptLogActionMetricKinds(action).length > 1;
}

export function getOptLogActionRowClass(action: OptLogVolumeActions): string {
  return isOptLogMultiMetricAction(action) ? optLogMultiMetricRowClass : '';
}

export type OptLogActionTableLayoutRow = OptLogVolumeActions & { _isSkeleton?: boolean };

function optLogActionStableRowIdParts(row: OptLogVolumeActions): string[] {
  return [row.volumeId, row.actionFinishTime, row.actionStartTime, row.type, row.instanceId].filter(
    Boolean
  ) as string[];
}

/** AG Grid `getRowId` for opt-log action rows (stable across polls). */
export function getOptLogActionGridRowId(row: OptLogVolumeActions): string {
  return optLogActionStableRowIdParts(row).join('-');
}

function layoutRowKey(row: OptLogVolumeActions): string {
  return optLogActionStableRowIdParts(row).join('|');
}

/** Matches `.opt-log-actions-table` CSS tokens (single-line row default). */
export const optLogActionSingleRowHeightPx = 60;

const optLogSnapshotMetricLinePx = 20;
const optLogSnapshotMetricGapPx = 6;
const optLogMultiMetricPaddingTopPx = 24;
const optLogMultiMetricPaddingBottomPx = 16;

/** Fixed row height from metric slot count (avoids autoHeight remeasure flicker on poll). */
export function getOptLogActionRowHeight(
  row: OptLogActionTableLayoutRow
): number {
  if (row._isSkeleton) {
    return optLogActionSingleRowHeightPx;
  }
  const slotCount = getOptLogActionMetricKinds(row).length;
  if (slotCount <= 1) {
    return optLogActionSingleRowHeightPx;
  }
  const contentHeight =
    slotCount * optLogSnapshotMetricLinePx +
    Math.max(0, slotCount - 1) * optLogSnapshotMetricGapPx;
  return optLogMultiMetricPaddingTopPx + contentHeight + optLogMultiMetricPaddingBottomPx;
}

/** Stable key for when AG Grid must remeasure rows (layout-affecting changes only). */
export function getOptLogActionTableLayoutSignature(rows: OptLogActionTableLayoutRow[]): string {
  if (rows.length === 0) {
    return '';
  }

  return rows
    .map((row) => {
      if (row._isSkeleton) {
        return 'skeleton';
      }
      const metricSlotCount = getOptLogActionMetricKinds(row).length;
      return `${layoutRowKey(row)}:${metricSlotCount}`;
    })
    .join(';');
}
